import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Upload } from "lucide-react";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";
import { Page, KPI, KPIGrid, Badge, Alert, Button, Input, Select, FilterField, FilterPanel, Table, Pagination, LoadingState, EmptyState, Text, formatPageRangeSummary, type Column } from "@marekl/adminer/components";
import { useBarcodeBank } from "../../lib/queries/barcodeBank";
import type { BarcodeBankRow, BarcodeBankState } from "../../lib/queries/barcodeBank";
import { useVoucherTypes } from "../../lib/queries/voucherTypes";
import { useDebouncedSearchFilter, useOffsetPaginatedQuery } from "@marekl/adminer/hooks";
import { formatDate } from "@marekl/adminer/format";
import { GenerateModal } from "./GenerateModal";
import { ImportModal } from "./ImportModal";
import { VoidConfirmModal } from "./VoidConfirmModal";

const LIMIT_OPTIONS: number[] = [25, 50, 100];
const DEFAULT_LIMIT = 50;

const STATE_LABELS: Record<BarcodeBankState, string> = {
  available: "Dostupný",
  assigned: "Přiřazený",
  voided: "Vyřazený",
};

const STATE_BADGE: Record<BarcodeBankState, "ok" | "info" | "muted"> = {
  available: "ok",
  assigned: "info",
  voided: "muted",
};

export function BarcodeBankPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [modal, setModal] = useState<"none" | "generate" | "import">("none");
  const [voidBarcode, setVoidBarcode] = useState<string | null>(null);

  const q = searchParams.get("q") ?? "";
  const typeFilter = searchParams.get("voucher_type_id") ?? "";
  const stateFilter = searchParams.get("state") ?? "";

  const { data: voucherTypes = [] } = useVoucherTypes();
  const filters = useMemo(
    () => ({
      search: q || undefined,
      voucher_type_id: typeFilter || undefined,
      state: stateFilter || undefined,
    }),
    [q, stateFilter, typeFilter],
  );
  const result = useOffsetPaginatedQuery(useBarcodeBank, filters, {
    searchParams,
    setSearchParams,
    defaultLimit: DEFAULT_LIMIT,
    limitOptions: LIMIT_OPTIONS,
    summaryFormatter: formatPageRangeSummary,
  });

  const { draft: qDraft, setDraft: setQDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => result.setFilters({ q: next }),
  });

  const items = result.data?.items ?? [];
  const kpis = result.data?.kpis ?? [];
  const lowStockKpis = kpis.filter((k) => k.available_count <= k.low_stock_threshold);

  const activeTypeName =
    typeFilter ? (voucherTypes.find((vt) => String(vt.id) === typeFilter)?.name ?? "") : "";

  const columns: Column<BarcodeBankRow>[] = [
    {
      key: "barcode",
      header: "EAN",
      primary: true,
      render: (row) => <Text as="code" variant="mono">{row.barcode}</Text>,
    },
    {
      key: "state",
      header: "Stav",
      render: (row) => (
        <Badge variant={STATE_BADGE[row.state]}>{STATE_LABELS[row.state]}</Badge>
      ),
    },
    {
      key: "imported_at",
      header: "Importováno",
      render: (row) => <Text variant="mono">{formatDate(row.imported_at)}</Text>,
    },
    {
      key: "assigned_voucher_id",
      header: "Přiřazeno",
      render: (row) =>
        row.assigned_voucher_id ? (
          <Text variant="mono">{row.assigned_voucher_id}</Text>
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
    {
      key: "voucher_customer",
      header: "Voucher / zákazník",
      render: () => <Text variant="muted">—</Text>,
    },
    {
      key: "actions",
      header: "",
      render: (row) =>
        <Table.Actions>
          {row.state === "available" ? (
            <Table.Action
              variant="danger"
              onClick={() => setVoidBarcode(row.barcode)}
            >
              Vyřadit
            </Table.Action>
          ) : (
            <Text variant="muted">—</Text>
          )}
        </Table.Actions>,
    },
  ];

  useSetTopbarCrumbs([{ label: "Banka EAN kódů" }]);

  return (
    <>
      <Page.Content>
        <Page.Header
          title="Banka EAN kódů"
          subtitle="předtištěné kódy fyzických voucherů"
          subtitleVariant="tagline"
          actions={
            <>
              <Button onClick={() => setModal("import")}>
                <Upload size={16} />
                Import dávky
              </Button>
              <Button variant="primary" onClick={() => setModal("generate")}>
                <Plus size={16} />
                Generovat
              </Button>
            </>
          }
        />

        {lowStockKpis.length > 0 && (
          <Alert variant="warn">
            {lowStockKpis.map((k, i) => (
              <span
                key={k.voucher_type_id}
                style={i > 0 ? { display: "block", marginTop: "0.25rem" } : {}}
              >
                Typu <strong>{k.name}</strong> zbývá pouze{" "}
                <strong>{k.available_count} dostupných kódů</strong> – objednejte další
                tisk.
              </span>
            ))}
          </Alert>
        )}

        {kpis.length > 0 && (
          <KPIGrid>
            {kpis.map((kpi) => {
              const isLow = kpi.available_count <= kpi.low_stock_threshold;
  return (
                <KPI
                  key={kpi.voucher_type_id}
                  label={kpi.name}
                  value={kpi.available_count}
                  delta={isLow ? "Dochází – objednat tisk" : "dostupných kódů"}
                  deltaDirection={isLow ? "down" : undefined}
                />
              );
            })}
          </KPIGrid>
        )}

        <FilterPanel>
            <FilterField wide>
              <Input
                label="Hledat kód"
                type="search"
                value={qDraft}
                placeholder="např. 8594000123…"
                onChange={(e) => setQDraft(e.target.value)}
              />
            </FilterField>
            <FilterField>
              <Select
                label="Typ"
                value={typeFilter}
                onChange={(e) =>
                  result.setFilter("voucher_type_id", e.target.value)
                }
              >
                <option value="">Všechny</option>
                {voucherTypes.map((vt) => (
                  <option key={vt.id} value={String(vt.id)}>
                    {vt.name}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField>
              <Select
                label="Stav"
                value={stateFilter}
                onChange={(e) => result.setFilter("state", e.target.value)}
              >
                <option value="">Všechny</option>
                <option value="available">Dostupný</option>
                <option value="assigned">Přiřazený</option>
                <option value="voided">Vyřazený</option>
              </Select>
            </FilterField>
        </FilterPanel>

        {/* Section header */}
        <Page.SectionHeader
          title={`Kódy${activeTypeName ? ` – ${activeTypeName}` : ""}`}
          meta={result.totalPages > 1 ? `stránka ${result.page} / ${result.totalPages}` : undefined}
        />

        {result.isLoading ? (
          <LoadingState />
        ) : (
          <Table
            cards
            columns={columns}
            rows={items}
            rowKey={(row) => row.barcode}
            error={result.isError ? "Nepodařilo se načíst banku kódů." : undefined}
            onRetry={result.refetch}
            empty={
              <EmptyState
                title="Žádné kódy"
                description="Pro zadané filtry neexistují žádné kódy."
              />
            }
          />
        )}

        <Pagination
          {...result.pagination}
        />
      </Page.Content>

      <GenerateModal open={modal === "generate"} onClose={() => setModal("none")} />
      <ImportModal open={modal === "import"} onClose={() => setModal("none")} />
      <VoidConfirmModal barcode={voidBarcode} onClose={() => setVoidBarcode(null)} />
    </>
  );
}
